import React, { FC, useState } from 'react';

import { CrossIcon } from '@modernloop/shared/icons';
import { IconButton, Stack, Theme, Typography } from '@mui/material';

import Link from 'src/components/Link';
import {
  SupportedBackgroundColor,
  SupportedTextColor,
  getBackgroundColorFromTheme,
  getTextColorFromTheme,
} from 'src/components/utils/color';

interface ThemeProps {
  backgroundColor?: SupportedBackgroundColor;
  color?: SupportedTextColor;
}

export type AppWideBannerAction = {
  label: string;
  href?: string;
  onClick?: () => void;
};

interface AppWideBannerProps extends ThemeProps {
  text: string | JSX.Element;
  action?: AppWideBannerAction;
  showClose?: boolean | null;
}

const AppWideBanner: FC<AppWideBannerProps> = ({ text, action, showClose, ...themeProps }) => {
  const [isClosed, setIsClosed] = useState(false);

  if (isClosed) {
    return null;
  }

  const labelJsx =
    typeof text === 'string' ? (
      <Typography
        sx={{
          color: (theme: Theme) =>
            themeProps.color ? getTextColorFromTheme(themeProps.color, theme) : theme.palette.primary.contrastText,
        }}
      >
        {text}
      </Typography>
    ) : (
      text
    );

  let actionJsx: JSX.Element | null = null;

  // linkable action
  if (action && action.href) {
    actionJsx = (
      <Link href={action?.href} target="_blank">
        <Typography
          sx={{
            color: (theme: Theme) =>
              themeProps.color ? getTextColorFromTheme(themeProps.color, theme) : theme.palette.primary.contrastText,
            marginLeft: '8px',
            textDecoration: 'underline',
          }}
        >
          {action?.label}
        </Typography>
      </Link>
    );
  }

  // clickable action
  if (action && action.onClick) {
    actionJsx = (
      // eslint-disable-next-line jsx-a11y/anchor-is-valid
      <Link onClick={action?.onClick}>
        <Typography
          sx={{
            color: (theme: Theme) =>
              themeProps.color ? getTextColorFromTheme(themeProps.color, theme) : theme.palette.primary.contrastText,
            marginLeft: '8px',
            textDecoration: 'underline',
          }}
        >
          {action?.label}
        </Typography>
      </Link>
    );
  }

  return (
    <Stack
      direction="row"
      sx={{
        padding: '8px 20px',
        backgroundColor: (theme: Theme) =>
          themeProps.backgroundColor
            ? getBackgroundColorFromTheme(themeProps.backgroundColor, theme)
            : theme.palette.primary.dark,
        borderBottom: (theme: Theme) => `1px solid ${theme.palette.divider}`,
      }}
      justifyContent="space-between"
      spacing={1}
    >
      <Stack direction="row" justifyContent="center" alignItems="center" width="100%">
        {labelJsx}
        {actionJsx}
      </Stack>
      {showClose && (
        <IconButton onClick={() => setIsClosed(true)} size="large">
          <CrossIcon />
        </IconButton>
      )}
    </Stack>
  );
};

export default AppWideBanner;
